// src/pages/Admin/SimpleWorkHistoryPage.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import "../../styles/Form.css"; // same wrapper styles you use on WorkHistoryPage
import "../../styles/WorkHistoryPage.css"; // optional if you want same table look

/**
 * Read-only Work History list (3 columns)
 * Shows:
 *  - Company/Type: if wh_type === "Working" -> wh_name, else wh_type
 *  - From: MM/YYYY
 *  - To: MM/YYYY or "Present"
 *
 * Props:
 *  - userId: number|string (prefer prop when embedded in slide)
 */
function SimpleWorkHistoryPage({ userId: userIdProp }) {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const userId = userIdProp || localStorage.getItem("userId");

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      if (!userId) return;
      setLoading(true);
      setError("");
      const res = await axios.get(`${BASE_URL}/api/work-history/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecords(res.data || []);
    } catch (err) {
      console.error("Fetch failed", err);
      setError("Failed to load work history.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const workingRecords = records.filter((rec) => rec.wh_type === "Working");

  return (
    <section className="simple-work-history-page">
      <header className="simple-work-history-header">
        <div>
          <h2>Work History</h2>
          <p>Employment records associated with this profile.</p>
        </div>
        {!loading && !error && (
          <span className="simple-work-history-count">
            {workingRecords.length} {workingRecords.length === 1 ? "record" : "records"}
          </span>
        )}
      </header>
      <div className="simple-work-history-body">

          {loading && <div className="text-muted">Loading…</div>}
          {error && <div className="text-danger mb-2">{error}</div>}

          <div className="table-responsive">
            <table className="table simple-work-history-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60%" }}>Company / Type</th>
                  <th style={{ width: "20%" }}>From</th>
                  <th style={{ width: "20%" }}>To</th>
                </tr>
              </thead>
              <tbody>
                {workingRecords.map((rec) => (
                    <tr key={rec.wh_id}>
                      <td>{rec.wh_name || "—"}</td>
                      <td>
                        {rec.wh_datefrom
                          ? dayjs(rec.wh_datefrom).format("MM/YYYY")
                          : "—"}
                      </td>
                      <td>
                        {rec.wh_dateto
                          ? dayjs(rec.wh_dateto).format("MM/YYYY")
                          : "Present"}
                      </td>
                    </tr>
                  ))}

                {!loading && !error && workingRecords.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted">
                      No records.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
      </div>
    </section>
  );
}

export default SimpleWorkHistoryPage;
